import { getLegoColor, LEGO_COLORS } from "../constants/legoColors";
import {
  LEGO_BRICK_TYPES,
  type LayerData,
  type OptimizedBrick,
} from "./brickOptimizer";
import { parseObjSafe, voxelizeMesh, type VoxelGrid } from "./voxelize";

export interface BuiltModel {
  layers: LayerData[];
  width: number;
  height: number;
}

/**
 * Full 3D-model pipeline: OBJ text → parsed mesh → solid voxel grid →
 * merged brick layers. Throws when the file has no usable geometry or
 * produces an empty build.
 */
export const buildFromObjText = (
  objText: string,
  options: {
    resolution?: number;
    colorIndex?: number;
  } = {},
): BuiltModel => {
  const mesh = parseObjSafe(objText);
  if (!mesh) throw new Error("no geometry");
  const grid: VoxelGrid = voxelizeMesh(mesh, options.resolution);
  const layers = buildModelLayers(grid, options.colorIndex);
  if (layers.length === 0) throw new Error("empty build");
  return { layers, width: grid.sizeX, height: grid.sizeY };
};

/**
 * Voxel grid → LEGO brick layers.
 *
 * Each voxel layer becomes one build layer. Solid runs are merged into the
 * largest bricks that fit (greedy first-fit, largest brick type first, both
 * orientations) so a 24×24 layer is tens of bricks, not 576 1×1s.
 * The model is mono-color in v1 (OBJ materials are a future step).
 */

export const DEFAULT_MODEL_COLOR_NAME = "Bright Red";

/** Name → palette index, falling back to White (0) rather than crashing. */
export const colorIndexByName = (name: string): number => {
  const index = LEGO_COLORS.findIndex((c) => c.name === name);
  return index >= 0 ? index : 0;
};

interface Placement {
  ok: boolean;
}

const tryPlace = (
  filled: Uint8Array,
  present: Uint8Array,
  sizeX: number,
  sizeY: number,
  x: number,
  y: number,
  w: number,
  h: number,
): Placement => {
  if (x + w > sizeX || y + h > sizeY) return { ok: false };
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      const i = (y + dy) * sizeX + (x + dx);
      if (!present[i] || filled[i]) return { ok: false };
    }
  }
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      filled[(y + dy) * sizeX + (x + dx)] = 1;
    }
  }
  return { ok: true };
};

/** Brick types largest-area first, with both orientations as candidates */
interface Candidate {
  type: (typeof LEGO_BRICK_TYPES)[number];
  w: number;
  h: number;
  rotation: 0 | 90;
}

const candidates: Candidate[] = LEGO_BRICK_TYPES.flatMap((type) => {
  const list: Candidate[] = [
    { type, w: type.width, h: type.height, rotation: 0 as const },
  ];
  if (type.width !== type.height) {
    list.push({ type, w: type.height, h: type.width, rotation: 90 as const });
  }
  return list;
}).sort((a, b) => b.w * b.h - a.w * a.h);

export const buildModelLayers = (
  grid: VoxelGrid,
  colorIndex: number = colorIndexByName(DEFAULT_MODEL_COLOR_NAME),
): LayerData[] => {
  const { data, sizeX, sizeY, sizeZ } = grid;
  const colorName = getLegoColor(colorIndex).name;
  const layers: LayerData[] = [];

  for (let z = 0; z < sizeZ; z++) {
    const present = new Uint8Array(sizeX * sizeY);
    let cells = 0;
    for (let y = 0; y < sizeY; y++) {
      for (let x = 0; x < sizeX; x++) {
        if (data[z * sizeY * sizeX + y * sizeX + x]) {
          present[y * sizeX + x] = 1;
          cells++;
        }
      }
    }
    if (cells === 0) continue;

    const filled = new Uint8Array(sizeX * sizeY);
    const bricks: OptimizedBrick[] = [];

    for (let y = 0; y < sizeY; y++) {
      for (let x = 0; x < sizeX; x++) {
        const i = y * sizeX + x;
        if (!present[i] || filled[i]) continue;
        for (const c of candidates) {
          if (tryPlace(filled, present, sizeX, sizeY, x, y, c.w, c.h).ok) {
            bricks.push({
              x,
              y,
              colorIndex,
              colorName,
              brickType: c.type,
              z,
              rotation: c.rotation,
            });
            break;
          }
        }
      }
    }

    if (bricks.length === 0) continue;
    layers.push({
      layer: z,
      bricks,
      brickCount: bricks.length,
      colorUsage: {},
    });
  }

  return layers;
};
